import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

// How long to keep re-measuring after a watched value changes, so the height follows a
// layout animation above the element (the Outstanding List's KPI collapse) frame by frame.
const FOLLOW_MS = 450

/**
 * The height that makes an element reach the bottom of the viewport, for a table that
 * should fill the page and scroll inside itself rather than run the page long (2026-10-01:
 * on the Outstanding List, paging past the end of a long table left the user stuck at
 * the bottom of the page, with the pagination cut off).
 *
 * Measured from where the element sits on the page, not where it is scrolled to, so the
 * page keeps one stable layout. `reserve` is the space to leave below it, in pixels, or a
 * function of the element that returns it, for space measured live (a table's own
 * pagination, the page's footer). `min` keeps a short viewport usable. The default, 400, is a
 * table header and about seven rows: below that the page scrolls instead, which beats a
 * table too short to work in.
 *
 * Re-measures on window resize, whenever the page's size changes, and for a moment after
 * any value in `watch` changes. A hidden element (an inactive tab) is skipped.
 *
 * @returns {[React.RefObject, number]} a ref for the element, and its height in pixels
 */
const useFillViewport = ({ reserve = 72, min = 400, watch = [] } = {}) => {
  const ref = useRef(null)
  const [height, setHeight] = useState(min)

  const measure = useCallback(() => {
    const element = ref.current
    if (!element || element.offsetParent === null) return
    const top = element.getBoundingClientRect().top + window.scrollY
    const below = typeof reserve === 'function' ? reserve(element) : reserve
    setHeight(Math.max(min, Math.floor(window.innerHeight - top - below)))
  }, [min, reserve])

  useLayoutEffect(() => {
    measure()
    let frame
    const until = performance.now() + FOLLOW_MS
    const follow = () => {
      measure()
      if (performance.now() < until) frame = requestAnimationFrame(follow)
    }
    frame = requestAnimationFrame(follow)
    return () => cancelAnimationFrame(frame)
  }, [measure, ...watch])

  useEffect(() => {
    window.addEventListener('resize', measure)
    const observer = new ResizeObserver(measure)
    observer.observe(document.documentElement)
    return () => {
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [measure])

  return [ref, height]
}

export default useFillViewport
